import { useMemo } from "react";
import { useParams } from "@tanstack/react-router";

import { useActiveOrgId, useOrgs, type OrgView } from "../../state/orgs";
import { resolveActiveOrgSegment } from "../../threadRoutes";
import { resolveRegulationOrg } from "./regulationOrg";

/**
 * The organization a Regulation page acts on.
 *
 * The compliance route's org segment is authoritative. On legacy routes
 * without a choice, the server's last-chosen organization is the fallback.
 * Resolution order, most specific first:
 *
 *   1. the route/last-picked id (`useActiveOrgId`) — the local, immediate answer;
 *   2. the org the *server* recorded as last chosen (`lastChosen` on the
 *      snapshot). Switching orgs writes this to `User.lastChosenOrgId`, so it
 *      is the only source that survives a different browser or machine —
 *      without it, a user who switched to Marinus yesterday lands on whatever
 *      `localStorage` happens to hold here, which is nothing.
 *
 * Deliberately **no fallback to the first org in the list**: alphabetical
 * order is not a tenant choice, and quietly rendering another organization's
 * policies (or flipping its monitoring switch) is worse than rendering
 * nothing. When nothing resolves, callers say so and let the user pick.
 */
export function useRegulationOrg(): { org: OrgView | null; isLoading: boolean } {
  const activeOrgId = useActiveOrgId();
  const { orgs, isPending } = useOrgs();
  const routeOrgSegment = useParams({
    strict: false,
    select: (params) => resolveActiveOrgSegment(params as { org?: string }),
  });

  return useMemo(
    () => resolveRegulationOrg(activeOrgId, orgs, isPending, routeOrgSegment !== null),
    [activeOrgId, isPending, orgs, routeOrgSegment],
  );
}
